import moment from "moment-timezone";
import { user } from "./constants/user";
import StorageUtils from "./utility/StorageUtils";
const axios = require("axios");

const baseURL = "/api";

let instance;
if (process.env.REACT_APP_NODE_ENV == "local") {
  instance = axios.create({
    timeout: 60000 * 4,
    headers: { "Content-Type": "application/json" },
  });
} else {
  instance = axios.create({
    baseURL,
    timeout: 60000 * 4,
    headers: { "Content-Type": "application/json" },
  });
}

instance.interceptors.request.use(
  (request) => {
    return request;
  },
  (error) => {
    console.log(error);
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.log(error);

    return Promise.reject(error);
  }
);

instance.defaults.crossdomain = true;
// axios.defaults.withCredentials = true;

// A 304 means the browser's HTTP cache validated and reconstructed the
// response body for us - that's a successful result, not a failure. Without
// this, axios's default validateStatus (200-299 only) rejects valid 304
// responses, and callers that fall back to a default value on error (e.g.
// getCurrentPlan treating any error as "free plan") misfire on cache hits.
instance.defaults.validateStatus = (status) => (status >= 200 && status < 300) || status === 304;

const timeZone = moment.tz.guess();

class HttpClient {
  constructor(token) {
    this.client = instance;
    this.token = token;
  }

  setToken(token) {
    this.token = token;
    return this;
  }

  getToken() {
    return this.token;
  }

  authHeaders() {
    return { Authorization: "Bearer " + this.token?.access_token };
  }

  // Sends a request and, on a 401, refreshes the token and retries once.
  // The API also answers 401 for reasons a new token won't fix (suspended
  // shop, plan upgrade needed, not authorized), so a second 401 goes back to
  // the caller instead of refreshing again — refreshing now succeeds and
  // rotates the token, so an unbounded retry would loop forever.
  withRefresh(send) {
    return send()
      .catch((error) => {
        if (error?.response?.status !== 401) throw error;
        return this.refreshToken().then((res) => {
          // refreshToken() already logged us out on failure.
          if (!res) throw error;
          return send();
        });
      })
      .then((res) => res.data);
  }

  get(endpoint) {
    if (!endpoint || /\/(null|undefined)(?=[/?&#]|$)/.test(String(endpoint))) return Promise.reject(new Error(`Blocked request: invalid endpoint "${endpoint}"`));
    return this.withRefresh(() => this.client.get("/v3" + endpoint, { headers: this.authHeaders() }));
  }

  post(endpoint, body) {
    if (!endpoint || /\/(null|undefined)(?=[/?&#]|$)/.test(String(endpoint))) return Promise.reject(new Error(`Blocked request: invalid endpoint "${endpoint}"`));
    if (!body) {
      body = {};
    }
    const reqBody = body.timeZone ? body : { ...body, timeZone };
    return this.withRefresh(() => this.client.post("/v3" + endpoint, reqBody, { headers: this.authHeaders() }));
  }

  put(endpoint, body) {
    if (!endpoint || /\/(null|undefined)(?=[/?&#]|$)/.test(String(endpoint))) return Promise.reject(new Error(`Blocked request: invalid endpoint "${endpoint}"`));
    if (!body) {
      body = {};
    }
    const reqBody = body.timeZone ? body : { ...body, timeZone };
    return this.withRefresh(() => this.client.put("/v3" + endpoint, reqBody, { headers: this.authHeaders() }));
  }

  delete(endpoint, body) {
    if (!endpoint || /\/(null|undefined)(?=[/?&#]|$)/.test(String(endpoint))) return Promise.reject(new Error(`Blocked request: invalid endpoint "${endpoint}"`));
    return this.withRefresh(() => this.client.delete("/v3" + endpoint, { data: body, headers: this.authHeaders() }));
  }

  getAccessToken(username, password) {
    return this.client({
      method: "post",
      url: "/v3/account/login",
      data: {
        email: username,
        password: password,
      },
    }).then((res) => {
      const data = res.data.email ? res.data : null;

      data?.token && StorageUtils.setToken(data?.token);

      // The token lives under its own key; keep no stale copy in the account blob.
      const { token, ...acct } = data || {};
      StorageUtils.setItem(StorageUtils.storeKey, data && acct);

      return data;
    });
  }

  initResendToken(email) {
    return this.client({
      method: "post",
      url: "/v3/2fa/resend",
      data: { email },
    }).then((res) => {
      return res.data;
    });
  }

  initTwoFactorVerification(email, token) {
    return this.client({
      method: "post",
      url: "/v3/2fa/verify",
      data: { email, token },
    })
      .then((res) => {
        const data = res.data.email ? res.data : null;

        // Same as a login without 2FA: without this the OTP step leaves no
        // token behind and Layout sends the user straight to /logout.
        if (data) {
          const { token, ...acct } = data;
          token && StorageUtils.setToken(token);
          StorageUtils.setItem(StorageUtils.storeKey, acct);
        }

        return data;
      })
      .catch((error) => {
        console.log("error here verificatioin", error);
        if (error && error.response && error.response.status === 401) {
          return null;
        }

        throw error;
      });
  }

  initTwoFactorLogin(username, password) {
    return this.client({
      method: "post",
      url: "/v3/2fa/login",
      data: {
        email: username,
        password: password,
      },
    })
      .then((res) => {
        const data = res.data.twoFactor || res.data.email ? res.data : null;
        data?.token && StorageUtils.setToken(data?.token);

        if (res.data.email) {
          const { token, ...acct } = data;
          StorageUtils.setItem(StorageUtils.storeKey, acct);
        }

        return data;
      })
      .catch((error) => {
        if (error && error.response && error.response.status === 401) {
          return null;
        }
        throw error;
      });
  }

  setExpirationTime(expires_in) {
    const exp = moment().utc().add({ seconds: expires_in }).format();
    this.expires = exp;
  }

  hasExpired() {
    const current = moment.utc().format();
    return moment(current).isAfter(this.expires);
  }

  // Revokes the access token and its refresh token chain on the API, so a
  // signed-out device can't refresh again. Fire-and-forget: sign-out
  // shouldn't wait on (or fail because of) the network.
  revokeToken(token = StorageUtils.getToken()) {
    if (token?.access_token) {
      this.client
        .post("/v3/access_token/revoke", { access_token: token.access_token, refresh_token: token.refresh_token })
        .catch((e) => e);
    } else if (token?.refresh_token) {
      this.client.post("/v3/refresh_token/revoke", { refresh_token: token.refresh_token }).catch((e) => e);
    }
  }

  logout() {
    // The token is revoked by the /logout route (Signout), which every
    // sign-out path ends on.
    this.client.get("/v3/account/logout").then((e) => e);
    StorageUtils.setItem(StorageUtils.storeKey, user);
    window.open("/logout", "_self");
  }

  // Handle expire token
  refreshToken() {
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    // Every refresh rotates the refresh token and the API treats a retired
    // one, used again, as stolen (it signs the device out). Another tab may
    // already have rotated it, so use what's in storage when it's newer.
    const stored = StorageUtils.getToken();
    if (stored?.refresh_token && stored.refresh_token !== this.token?.refresh_token) {
      this.token = stored;
      return Promise.resolve(this.token);
    }

    const refresh_token = this.token?.refresh_token;
    if (!refresh_token) {
      this.logout();
      return Promise.resolve(null);
    }

    this.isRefreshing = true;
    this.refreshPromise = this.client
      .post("/v3/token/refresh", { refresh_token })
      .then((res) => {
        this.token = res.data;
        StorageUtils.setToken(res.data);
        return this.token;
      })
      .catch((error) => {
        if (String(error).includes("401") || String(error).includes("400")) {
          this.logout();
          return null;
        }
        throw error;
      })
      .finally(() => {
        this.isRefreshing = false;
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }
}

const Http = new HttpClient(null);
Http.isRefreshing = false;
Http.refreshPromise = null;

export class HttpFactory {
  static getInstance() {
    const token = {
      token_type: "bearer",
      expires_in: 300,
      access_token: null,
      refresh_token: null,
    };

    Http.setToken(StorageUtils.getToken() || token);

    return Http;
  }
}

export default HttpClient;

export { instance as AxiosInstance };
